import { useState, useMemo } from 'react'
import { resolveTableCellValue } from '../../utils/table-cell-value'

interface Column {
  key: string
  label: string
  sortable?: boolean
  format?: (value: any) => string
}

interface DataTableProps {
  data: Array<Record<string, any>>
  columns: Column[]
  pageSize?: number
  title?: string
  loading?: boolean
}

export function DataTable({
  data,
  columns,
  pageSize = 10,
  title,
  loading = false,
}: DataTableProps) {
  // Forzar pageSize máximo de 10
  const effectivePageSize = Math.min(pageSize, 10)
  

  const [currentPage, setCurrentPage] = useState(1)
  const [sortConfig, setSortConfig] = useState<{
    key: string
    direction: 'asc' | 'desc'
  } | null>(null)

  const sortedData = useMemo(() => {
    if (!sortConfig) return data

    return [...data].sort((a, b) => {
      const aVal = resolveTableCellValue(a, sortConfig.key)
      const bVal = resolveTableCellValue(b, sortConfig.key)

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortConfig])

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * effectivePageSize
    return sortedData.slice(start, start + effectivePageSize)
  }, [sortedData, currentPage, effectivePageSize])

  const totalPages = Math.ceil(data.length / effectivePageSize)

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  if (loading) {
    return (
      <div className="bg-white border rounded-lg overflow-hidden">
        {title && (
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
        )}
        <div className="p-12 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border rounded-lg overflow-hidden">
        {title && (
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
        )}
        <div className="p-12 flex items-center justify-center text-gray-500">
          No hay datos disponibles
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border rounded-lg overflow-hidden h-full flex flex-col">
      {title && (
        <div className="px-6 py-4 border-b flex-shrink-0">
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
      )}

      <div className="overflow-x-auto flex-1 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  onClick={() => col.sortable && handleSort(col.key)}
                  style={{ cursor: col.sortable ? 'pointer' : 'default' }}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    {col.sortable && sortConfig?.key === col.key && (
                      <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {paginatedData.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {columns.map((col) => {
                  const raw = resolveTableCellValue(row, col.key)
                  const display =
                    raw === undefined || raw === null
                      ? ''
                      : col.format
                        ? col.format(raw)
                        : String(raw)
                  return (
                    <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm">
                      {display}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="px-6 py-4 border-t flex items-center justify-between flex-shrink-0 bg-white">
        <p className="text-sm text-gray-600">
          Mostrando {(currentPage - 1) * effectivePageSize + 1} -{' '}
          {Math.min(currentPage * effectivePageSize, data.length)} de {data.length} registros
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  )
}
